// -------------------------------
// EXPENSE (REIMBURSEMENT) INTERFACES
// -------------------------------
export interface PendingExpense {
    expense_name?: string;
    expense_date?: string; // YYYY-MM-DD
    amount?: string;
    payment_method?: string;
}

export interface PendingDeleteExpense {
    awaitingName?: boolean;
}
// -------------------------------
// CREATE EXPENSE — START
// -------------------------------
export const startCreateReimbursementFlow = (
    setPending: (data: PendingExpense | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({ expense_name: nameFromIntent });

        pushAIChat(`When was "${nameFromIntent}" incurred?`);
        speak(`When was ${nameFromIntent} incurred?`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What is the reimbursement name?");
    speak("What is the reimbursement name?");
};
// -------------------------------
// CREATE EXPENSE — PROCESS
// -------------------------------
export const processPendingReimbursement = (
    text: string,
    pending: PendingExpense,
    setPending: (data: PendingExpense | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — EXPENSE NAME
    if (!pending.expense_name) {
        const cleanName = text.trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid reimbursement name.");
            speak("Please provide a valid reimbursement name.");
            return;
        }

        setPending({ ...pending, expense_name: cleanName });

        pushAIChat("What is the date of the expense?");
        speak("What is the date of the expense?");
        return;
    }

    // STEP 2 — DATE
    if (!pending.expense_date) {
        const date = text.trim();

        if (!date) {
            pushAIChat("Please provide a valid date.");
            speak("Please provide a valid date.");
            return;
        }

        setPending({ ...pending, expense_date: date });

        pushAIChat("What is the amount?");
        speak("What is the amount?");
        return;
    }

    // STEP 3 — AMOUNT
    if (!pending.amount) {
        const amount = text.replace(/[^0-9.]/g, "");

        if (!amount) {
            pushAIChat("Please provide a valid amount.");
            speak("Please provide a valid amount.");
            return;
        }

        setPending({ ...pending, amount });

        pushAIChat("What payment method was used?");
        speak("What payment method was used?");
        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "reimbursement-payment-method" }
            })
        );
        return;
    }

};
// -------------------------------
// DELETE EXPENSE — START
// -------------------------------
export const startDeleteReimbursementFlow = (
    setPendingDelete: (data: PendingDeleteExpense | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting reimbursement "${nameFromIntent}"...`);
        speak(`Deleting reimbursement ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-accounting-expense", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which reimbursement would you like to delete?");
    speak("Which reimbursement would you like to delete?");
};
// -------------------------------
// DELETE EXPENSE — PROCESS
// -------------------------------
export const processPendingDeleteReimbursement = (
    text: string,
    pending: PendingDeleteExpense,
    setPendingDelete: (data: PendingDeleteExpense | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid reimbursement name.");
        speak("Please provide a valid reimbursement name.");
        return;
    }

    pushAIChat(`Deleting reimbursement "${cleanName}"...`);
    speak(`Deleting reimbursement ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-accounting-expense", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};
